// 门户发了一次性验证码：不是缺字段，不走 AI 找文件。
// 邮箱验证码只认转发 —— 担保人的 ICA 来信转发到 ss@kaider.sg，后端自动读码填入；不给手填，
// 没转发过来就停在这里请担保人设好转发（以后申诉结果也靠这条转发）。短信验证码人手填
import { useEffect, useState } from "react";
import type { OtpAsk, TaskDTO } from "@csp/shared";
import { api, errorText } from "../lib/api";

export function OtpPrompt({ task, otp, onDone, onChanged }: { task: TaskDTO; otp: OtpAsk; onDone: () => void; onChanged?: () => void }) {
  const digits = otp.digits ?? 6;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  /** 页面上的倒计时（重发后后端读回）：到期时刻 */
  const [deadline, setDeadline] = useState<number | null>(null);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  // 从停下来等验证码那一刻算有效期
  const end = deadline ?? Date.parse(task.statusSince ?? task.updatedAt) + (otp.expiresSec ?? 180) * 1000;
  const left = Math.max(0, Math.round((end - now) / 1000));
  const where = otp.to || (otp.channel === "sms" ? "担保人手机" : "担保人邮箱");
  const [info, setInfo] = useState<string | null>(null);
  /** 后端直接在页面上填验证码 / 点重新发送，几秒就有结果；通过了才交回执行端 */
  const act = async (body: { code?: string; resend?: boolean }) => {
    setBusy(true); setError(null); setInfo(null);
    try {
      const r = await api.otp(task.id, body);
      if (r.state === "verified" || r.state === "unknown" || r.state === "handed_over") { onDone(); return; }
      const until = (sec: number | null | undefined, fallback: number | null) => (sec != null ? Date.now() + sec * 1000 : fallback);
      if (r.state === "resent") { setCode(""); setDeadline(until(r.leftSec, Date.now() + (otp.expiresSec ?? 180) * 1000)); setInfo(otp.channel === "email" ? "已在政府页面上点了「Resend OTP」，正在等新验证码转发到 ss@kaider.sg" : "已在政府页面上点了「Resend OTP」，倒计时已重新开始：去手机看最新一条验证码"); if (otp.forwardFailedAt) onChanged?.(); return; }
      if (r.state === "resend_ignored") { setDeadline(until(r.leftSec, deadline)); setInfo(`政府页面还在倒计时，暂时不让重发：上一封验证码还有效${r.leftSec != null ? `（约 ${Math.floor(r.leftSec / 60)}:${String(r.leftSec % 60).padStart(2, "0")}）` : ""}，用最新一封里的；倒计时结束后再点重新发送`); return; }
      setError(r.state === "expired" ? "这个验证码已经过期了：点「重新发送」拿新的" : r.state === "invalid" ? "验证码不对，请再核对一下（注意大小写、别多复制空格）" : "页面上没找到验证码输入框，请看实时画面");
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  // 普通输入框：粘贴什么就是什么；验证码可能有字母（ICA 是 xkmv3v 这样），提交时只去掉空格
  const digitsOnly = code.replace(/\s/g, "");
  const ok = digitsOnly.length >= 4;
  if (otp.channel === "email") {
    const sponsor = otp.sponsorEmail || otp.to || "担保人邮箱";
    // 有效期过了再多等 1 分钟还没转发过来，后端就判定转发没设好
    const judgeLeft = Math.max(0, left + 60);
    return (
      <div className="otp-box" data-testid="otp-prompt">
        {otp.forwardFailedAt ? (
          <>
            <p className="otp-where err">✕ ss@kaider.sg 没收到转发来的验证码：担保人 <b translate="no">{sponsor}</b> 的 ICA 邮件转发没设好。</p>
            <p className="small">请担保人在邮箱里设好「来自 ica.gov.sg 的邮件自动转发到 ss@kaider.sg」，设好后点下面的按钮：系统会在政府页面重新发送验证码，收到后自动填入。以后申诉结果也要靠这条转发才知道。</p>
          </>
        ) : (
          <>
            <p className="otp-where">📧 政府网站已把验证码发到 <b translate="no">{where}</b>，正在等它转发到 <b>ss@kaider.sg</b>，收到后自动填入，不用手动输入。</p>
            <p className="small muted">{judgeLeft > 0 ? `大约 ${Math.floor(judgeLeft / 60)}:${String(judgeLeft % 60).padStart(2, "0")} 内没收到，就判定担保人邮箱转发没设好` : "正在判定转发是否设好…"}</p>
          </>
        )}
        <div className="row" style={{ gap: 8 }}>
          <button className={`btn${otp.forwardFailedAt ? " btn-primary btn-big" : ""}`} disabled={busy} onClick={() => void act({ resend: true })}>
            {busy ? "正在重新发送…" : otp.forwardFailedAt ? "已设好转发，重新发送" : "重新发送"}
          </button>
        </div>
        {info && <p className="small ok-text">{info}</p>}
        {error && <p className="small err">{error}</p>}
      </div>
    );
  }
  return (
    <div className="otp-box" data-testid="otp-prompt">
      <p className="otp-where">{otp.channel === "sms" ? "📱" : "📧"} 政府网站已把 <b>验证码</b> 发到 <b translate="no">{where}</b>，请去{otp.channel === "sms" ? "手机短信" : "邮箱"}里看，填到下面：</p>
      <input className="otp-input" aria-label="验证码" autoFocus placeholder="粘贴或输入验证码" value={code} disabled={busy}
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && ok) void act({ code: digitsOnly }); }} />
      <p className={`small ${left > 0 ? "muted" : "err"}`}>{left > 0 ? `大约还剩 ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")} 有效` : "可能已经过期了：点「重新发送」拿新的验证码"}</p>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-primary btn-big" disabled={busy || !ok} onClick={() => void act({ code: digitsOnly })}>{busy ? "正在填进页面…" : "填入验证码，继续"}</button>
        <button className="btn" disabled={busy} onClick={() => void act({ resend: true })}>过期了 / 没收到，重新发送</button>
      </div>
      {info && <p className="small ok-text">{info}</p>}
      {error && <p className="small err">{error}</p>}
    </div>
  );
}
